import { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Ban, X, Check, AlertCircle, Loader2 } from 'lucide-react';
import { useGeneration } from '../context/GenerationContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { GenerationTask } from '../types/generation';
import { tasksApi } from '../services/api';
import { TaskResultService } from '../services/taskResultService';
import { useTasks } from '../hooks/useTasks';

export function GenerationStatus() {
  const { 
    cancelTask
  } = useGeneration();
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  
  // Состояние для оптимистичного обновления UI
  const [locallyViewedTasks, setLocallyViewedTasks] = useState<Set<string>>(new Set());
  
  // Используем новый хук для получения задач
  const { data: tasksData, refetch } = useTasks();
  const allTasksFromContext = tasksData?.tasks || [];

  const markTaskAsViewed = useCallback(async (taskId: string) => {
    // Оптимистично помечаем задачу как просмотренную локально
    setLocallyViewedTasks(prev => new Set([...prev, taskId]));
    
    try {
      // Отправляем запрос на сервер
      await tasksApi.markTaskViewed(taskId);
      
      // Принудительно обновляем кэш после успешного запроса
      setTimeout(() => {
        refetch();
      }, 100);
      
    } catch (error: any) {
      // Если ошибка 404 (задача уже просмотрена), это нормально - оставляем в локальном состоянии
      if (error?.response?.status === 404) {
        return;
      }
      
      // Для других ошибок убираем из локального состояния
      setLocallyViewedTasks(prev => {
        const newSet = new Set(prev);
        newSet.delete(taskId);
        return newSet;
      });
    }
  }, [refetch]);

  useEffect(() => {
    // Данные теперь загружаются через React Query автоматически
    // SignalR обновления обрабатываются в GenerationContext
    setIsLoading(false);
  }, []);

  const tasksToShow = allTasksFromContext
    .filter((task: GenerationTask) => {
      // Исключаем задачи без ID или с пустым ID
      if (!task.id || task.id.trim() === '') {
        return false;
      }
      
      // Исключаем задачи, которые уже просмотрены на сервере
      if (task.isViewed === true) {
        return false;
      }
      
      // Исключаем задачи, которые мы локально пометили как просмотренные
      if (locallyViewedTasks.has(task.id)) {
        return false;
      }
      
      return true;
    })
    // Фильтруем дубликаты по ID
    .filter((task: GenerationTask, index: number, arr: GenerationTask[]) => arr.findIndex((t: GenerationTask) => t.id === task.id) === index)
    .sort((a: GenerationTask, b: GenerationTask) => {
      if (a.status === 'pending' && b.status !== 'pending') return -1;
      if (a.status !== 'pending' && b.status === 'pending') return 1;
      if (a.status === 'completed' && b.status === 'completed') {
        return (b.completedAt || 0) - (a.completedAt || 0);
      }
      return 0;
    })
    .slice(0, 2);

  if (location.pathname === '/tasks') return null;
  if (isLoading && tasksToShow.length === 0) return null;
  if (tasksToShow.length === 0) {
    return null;
  }

  const handleViewResult = async (taskId: string, _type: string) => {
    // Помечаем задачу как просмотренную перед навигацией
    await markTaskAsViewed(taskId);
    
    // Находим задачу чтобы получить knowledgeDocumentId
    const task = allTasksFromContext.find((t: GenerationTask) => t.id === taskId);
    let knowledgeDocumentId = task?.knowledgeDocumentId;

    console.log('GenerationStatus handleViewResult:', {
      taskId,
      knowledgeDocumentId,
      task: task ? { id: task.id, title: task.title, knowledgeDocumentId: task.knowledgeDocumentId } : null
    });

    // Если knowledgeDocumentId отсутствует или невалиден, пробуем получить через TaskResultService
    if (!knowledgeDocumentId || knowledgeDocumentId === 'root' || knowledgeDocumentId.trim() === '') {
      try {
        const resultInfo = await TaskResultService.getTaskResultInfo(taskId, knowledgeDocumentId);
        if (resultInfo?.documentId) {
          knowledgeDocumentId = resultInfo.documentId;
        }
      } catch (e) {
        console.warn('GenerationStatus: не удалось получить knowledgeDocumentId через TaskResultService', e);
      }
    }

    // Проверяем, что knowledgeDocumentId существует и не является 'root' или пустым
    if (knowledgeDocumentId && knowledgeDocumentId !== 'root' && knowledgeDocumentId.trim() !== '') {
      console.log(`Переход к документу: /knowledge/${knowledgeDocumentId}`);
      navigate(`/knowledge/${knowledgeDocumentId}`);
    } else {
      console.log('Нет валидного knowledgeDocumentId, переход к /knowledge');
      navigate('/knowledge');
    }
  };

  return (
    <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 hidden sm:block">
      <motion.div
        key="generation-status-container"
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -100, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 260, damping: 20 }}
        className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl px-6 py-3"
      >
        <div className="flex flex-col space-y-2">
          {tasksToShow.map((task: GenerationTask) => (
            <motion.div
              key={`task-${task.id}`}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="flex items-center space-x-3"
            >
              {task.status === 'pending' ? (
                <>
                  <div className="relative">
                    <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                    <motion.div
                      className="absolute inset-0 rounded-full border-2 border-blue-500/30"
                      animate={{
                        scale: [1, 1.2, 1],
                        opacity: [1, 0.5, 1]
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {task.name || task.title}
                  </span>
                  {task.canCancel && (
                    <button
                      onClick={() => cancelTask(task.id)}
                      className="p-1 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-full"
                      title="Отменить задачу"
                    >
                      <Ban className="w-4 h-4" />
                    </button>
                  )}
                </>
              ) : task.status === 'cancelled' ? (
                <>
                  <div className="relative">
                    <Ban className="w-5 h-5 text-red-500" />
                  </div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {task.name || task.title}
                  </span>
                  <button
                    onClick={() => markTaskAsViewed(task.id)}
                    className="p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
                    title="Закрыть"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </>
              ) : task.status === 'error' ? (
                <>
                  <div className="relative">
                    <AlertCircle className="w-5 h-5 text-red-500" />
                  </div>
                  <div className="flex flex-col min-w-0 flex-1">
                    <span className="text-sm font-medium text-gray-900 dark:text-white break-words">
                      {task.name || task.title}
                    </span>
                    <span className="text-xs text-red-500 dark:text-red-400">
                      При выполнении задачи произошла ошибка. Перезапустите задачу.
                    </span>
                  </div>
                  <button
                    onClick={() => markTaskAsViewed(task.id)}
                    className="p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full flex-shrink-0"
                    title="Закрыть"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </>
              ) : (
                <>
                  <div className="relative">
                    <Check className="w-5 h-5 text-green-500" />
                  </div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {task.name || task.title}
                  </span>
                  <span
                    onClick={() => handleViewResult(task.id, task.type)}
                    className="text-sm text-blue-600 dark:text-blue-400 cursor-pointer"
                  >
                    Результат
                  </span>
                  <button
                    onClick={() => markTaskAsViewed(task.id)}
                    className="p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
                    title="Закрыть"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </>
              )}
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}